import { useEffect, useMemo, useState } from "react";

import Button from "./ui/Button";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Textarea from "./ui/Textarea";
import { Card, CardBody, CardHeader } from "./ui/Card";
import { Chip } from "./ui/Chip";

import CountryFlag from "./CountryFlag";

import {
  CATEGORY_GROUPS,
  CATEGORY_INFO,
  STAGES,
  STAGE_INFO,
} from "../lib/saints/constants";
import { COUNTRY_SELECT_GROUPS, countryOption } from "../lib/saints/countries";
import { monthDayToKey, parseFeastKey } from "../lib/saints/feasts";
import { isValidISODate, slugify, splitList, splitMultiline } from "../lib/text";
import { isValidUrl, normalizeUrl } from "../lib/urls";
import { arrayMove } from "../lib/arrays";
import { tokensFromName, substringsFromTokens } from "../lib/saints/nameIndex";
import { normalizeOuterDoubleQuotes } from "../lib/quotes";

function SectionTitle({ eyebrow, title, hint }) {
  return (
    <div>
      {eyebrow ? (
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-dulia-nightSoft dark:text-dulia-goldSoft">
          {eyebrow}
        </div>
      ) : null}
      <h2 className="mt-1 text-lg font-semibold text-dulia-ink dark:text-dulia-parchment">
        {title}
      </h2>
      {hint ? (
        <p className="mt-1 text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function FieldBlock({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
        {label}
      </label>
      {children}
      {hint ? (
        <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/** ---------- tiny hover blurb wrapper ---------- */
function HoverBlurb({ title, blurb, children }) {
  const text = blurb || "";
  return (
    <div className="group relative inline-block">
      {text ? (
        <div className="pointer-events-none absolute left-1/2 top-0 z-20 hidden w-72 -translate-x-1/2 -translate-y-[calc(100%+0.5rem)] rounded-2xl border border-dulia-line bg-dulia-panel p-3 text-xs shadow-card group-hover:block group-focus-within:block dark:border-dulia-lineStrong/30 dark:bg-dulia-night">
          <div className="font-semibold text-dulia-ink dark:text-dulia-parchment">
            {title}
          </div>
          <div className="mt-1 leading-snug text-dulia-navy/80 dark:text-dulia-parchment/75">
            {text}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** ---------- Gallery editor (visual) ---------- */
function GalleryEditor({ items, setItems }) {
  const [newUrls, setNewUrls] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  function addFromInput() {
    const incoming = splitMultiline(newUrls);
    if (!incoming.length) return;
    const cleaned = incoming.map(normalizeUrl).filter(Boolean);
    setItems((prev) => [...prev, ...cleaned.map((url) => ({ url, caption: "" }))]);
    setNewUrls("");
  }

  function removeAt(i) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateCaptionAt(i, caption) {
    setItems((prev) =>
      prev.map((x, idx) => (idx === i ? { ...x, caption } : x))
    );
  }

  function onDragStart(i) {
    setDragIndex(i);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  function onDrop(i) {
    if (dragIndex === null || dragIndex === i) return;
    setItems((prev) => arrayMove(prev, dragIndex, i));
    setDragIndex(null);
  }

  return (
    <Card className="overflow-hidden rounded-[28px]">
      <CardHeader className="px-5 py-4">
        <SectionTitle
          eyebrow="Images"
          title="Gallery"
          hint="Paste one or more image URLs and optionally add captions. You can drag images to reorder. The first image in the list will be the hero image (the one displayed at the top of the profile and in search results)."
        />
      </CardHeader>

      <CardBody className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
            Gallery URLs
          </label>
          <Textarea
            rows={4}
            className="w-full font-mono text-xs"
            placeholder={"Paste one image URL per line\nhttps://...\nhttps://..."}
            value={newUrls}
            onChange={(e) => setNewUrls(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={addFromInput}>
              + Add URL(s)
            </Button>
            <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
              Drag cards to reorder.
            </div>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border border-dulia-line bg-dulia-parchment/60 p-3 text-sm text-dulia-navy/80 dark:border-dulia-lineStrong/20 dark:bg-dulia-night dark:text-dulia-parchment/75">
            No images yet.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((item, i) => {
              const url = item?.url || "";
              const caption = item?.caption || "";
              const valid = isValidUrl(url);

              return (
                <div
                  key={`${url}-${i}`}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(i)}
                  className="group overflow-hidden rounded-2xl border border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night"
                  title="Drag to reorder"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-dulia-line bg-dulia-parchment/55 px-3 py-2 dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft">
                    <div className="flex items-center gap-2">
                      <span className="select-none text-dulia-navy/55 dark:text-dulia-parchment/50">
                        ≡
                      </span>
                      <span className="text-xs text-dulia-navy/75 dark:text-dulia-parchment/70">
                        #{i + 1}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAt(i)}
                      className="rounded-lg px-2 py-1 text-xs text-dulia-navy transition hover:bg-dulia-parchment dark:text-dulia-parchment dark:hover:bg-dulia-night"
                      title="Remove"
                    >
                      Delete
                    </button>
                  </div>

                  <div className="space-y-3 p-3">
                    <div className="break-all font-mono text-[11px] text-dulia-navy/75 dark:text-dulia-parchment/72">
                      {url}
                    </div>

                    {!valid ? (
                      <div className="text-xs text-dulia-danger">Invalid URL</div>
                    ) : (
                      <img
                        src={url}
                        alt={`gallery ${i + 1}`}
                        className="h-44 w-full rounded-xl object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}

                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-dulia-ink dark:text-dulia-parchment">
                        Caption (optional)
                      </label>
                      <Input
                        value={caption}
                        onChange={(e) => updateCaptionAt(i, e.target.value)}
                        placeholder="e.g. Portrait, Icon, Shrine..."
                      />
                    </div>

                    {url && valid ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-dulia-navy/75 underline decoration-dulia-gold/50 underline-offset-4 hover:text-dulia-navy dark:text-dulia-parchment/75 dark:hover:text-dulia-parchment"
                        title={url}
                      >
                        Open source
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** ---------- Feast days editor (multi) ---------- */
function FeastDaysEditor({ keys, setKeys }) {
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const maxDay = useMemo(() => {
    const m = Number(month) || null;
    if (!m) return 31;
    if ([4, 6, 9, 11].includes(m)) return 30;
    if (m === 2) return 29;
    return 31;
  }, [month]);

  function addFeast() {
    const m = month ? Number(month) : null;
    const d = day ? Number(day) : null;
    if (!m || !d) return;

    const key = monthDayToKey(m, d);
    const parsed = parseFeastKey(key);
    if (!parsed) return;

    setKeys((prev) => (prev.includes(parsed.key) ? prev : [...prev, parsed.key]));
    setMonth("");
    setDay("");
  }

  function removeAt(i) {
    setKeys((prev) => prev.filter((_, idx) => idx !== i));
  }

  function onDragStart(i) {
    setDragIndex(i);
  }

  function onDrop(i) {
    if (dragIndex === null || dragIndex === i) return;
    setKeys((prev) => arrayMove(prev, dragIndex, i));
    setDragIndex(null);
  }

  return (
    <div className="space-y-3">
      <FieldBlock
        label="Feast Day(s)"
        hint="Add one or many feast days, then drag to reorder."
      >
        <div className="grid gap-2 sm:grid-cols-4">
          <Select
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setDay("");
            }}
          >
            <option value="">Month…</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>

          <Select
            value={day}
            onChange={(e) => setDay(e.target.value)}
            disabled={!month}
            title={!month ? "Pick a month first" : ""}
          >
            <option value="">Day…</option>
            {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>

          <Button type="button" onClick={addFeast}>
            + Add feast
          </Button>

          <div className="self-center text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
            Drag to reorder
          </div>
        </div>
      </FieldBlock>

      {keys.length === 0 ? (
        <div className="rounded-2xl border border-dulia-line bg-dulia-parchment/60 p-3 text-sm text-dulia-navy/80 dark:border-dulia-lineStrong/20 dark:bg-dulia-night dark:text-dulia-parchment/75">
          No feast days set.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {keys.map((k, i) => (
            <div
              key={`${k}-${i}`}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className="inline-flex items-center gap-2 rounded-full border border-dulia-line bg-dulia-panel px-3 py-1.5 text-sm text-dulia-ink shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment"
              title="Drag to reorder"
            >
              <span className="select-none text-dulia-navy/55 dark:text-dulia-parchment/50">
                ≡
              </span>
              <span className="font-mono text-xs">{k}</span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="rounded-md px-2 py-1 text-xs text-dulia-navy transition hover:bg-dulia-parchment dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft"
                title="Remove"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** ---------- Quotes/Prayers list editor ---------- */
function splitByBlankLines(text) {
  return String(text || "")
    .split(/\n\s*\n+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function TextListEditor({
  label,
  help,
  addHelp,
  items,
  setItems,
  addMode = "blankLines", // "lines" | "blankLines"
  itemRows = 3,
  placeholder,
}) {
  const [draft, setDraft] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const splitFn = addMode === "lines" ? splitMultiline : splitByBlankLines;

  function addDraft() {
    const incoming = splitFn(draft);
    if (!incoming.length) return;
    setItems((prev) => [...prev, ...incoming]);
    setDraft("");
  }

  function removeAt(i) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateAt(i, value) {
    setItems((prev) => prev.map((x, idx) => (idx === i ? value : x)));
  }

  function onDragStart(i) {
    setDragIndex(i);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  function onDrop(i) {
    if (dragIndex === null || dragIndex === i) return;
    setItems((prev) => arrayMove(prev, dragIndex, i));
    setDragIndex(null);
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
          {label}
        </div>
        {help ? (
          <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
            {help}
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Textarea
          rows={4}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={addDraft}>
            + Add
          </Button>
          {addHelp ? (
            <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
              {addHelp}
            </div>
          ) : null}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dulia-line bg-dulia-parchment/60 p-3 text-sm text-dulia-navy/80 dark:border-dulia-lineStrong/20 dark:bg-dulia-night dark:text-dulia-parchment/75">
          None yet.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
            Drag cards to reorder.
          </div>
          {items.map((text, i) => (
            <div
              key={`${i}`}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(i)}
              className="overflow-hidden rounded-2xl border border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night"
              title="Drag to reorder"
            >
              <div className="flex items-center justify-between gap-2 border-b border-dulia-line bg-dulia-parchment/55 px-3 py-2 dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft">
                <div className="flex items-center gap-2">
                  <span className="select-none text-dulia-navy/55 dark:text-dulia-parchment/50">
                    ≡
                  </span>
                  <span className="text-xs text-dulia-navy/75 dark:text-dulia-parchment/70">
                    #{i + 1}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="rounded-lg px-2 py-1 text-xs text-dulia-navy transition hover:bg-dulia-parchment dark:text-dulia-parchment dark:hover:bg-dulia-night"
                >
                  Delete
                </button>
              </div>
              <div className="p-3">
                <Textarea
                  rows={itemRows}
                  value={text}
                  onChange={(e) => updateAt(i, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** ---------- Patronage editor (like Quotes) ---------- */
function PatronageEditor({ items, setItems }) {
  const [draft, setDraft] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  function addDraft() {
    const lines = splitMultiline(draft);
    const incoming = lines
      .flatMap((line) => splitList(line))
      .map((x) => String(x || "").trim())
      .filter(Boolean);

    if (!incoming.length) return;

    setItems((prev) => [...prev, ...incoming]);
    setDraft("");
  }

  function removeAt(i) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateAt(i, value) {
    setItems((prev) => prev.map((x, idx) => (idx === i ? value : x)));
  }

  function onDragStart(i) {
    setDragIndex(i);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  function onDrop(i) {
    if (dragIndex === null || dragIndex === i) return;
    setItems((prev) => arrayMove(prev, dragIndex, i));
    setDragIndex(null);
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
          Patronage
        </div>
        <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
          Add one or more patronages, comma-separated or one per line. Drag cards to reorder.
        </div>
      </div>

      <div className="space-y-1.5">
        <Textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={'e.g. "students, sailors"\n(or one per line)'}
        />
        <div className="flex items-center gap-2">
          <Button type="button" onClick={addDraft}>
            + Add
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dulia-line bg-dulia-parchment/60 p-3 text-sm text-dulia-navy/80 dark:border-dulia-lineStrong/20 dark:bg-dulia-night dark:text-dulia-parchment/75">
          None yet.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
            Drag cards to reorder.
          </div>

          {items.map((text, i) => (
            <div
              key={`${i}`}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(i)}
              className="overflow-hidden rounded-2xl border border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night"
              title="Drag to reorder"
            >
              <div className="flex items-center justify-between gap-2 border-b border-dulia-line bg-dulia-parchment/55 px-3 py-2 dark:border-dulia-lineStrong/20 dark:bg-dulia-nightSoft">
                <div className="flex items-center gap-2">
                  <span className="select-none text-dulia-navy/55 dark:text-dulia-parchment/50">
                    ≡
                  </span>
                  <span className="text-xs text-dulia-navy/75 dark:text-dulia-parchment/70">
                    #{i + 1}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="rounded-lg px-2 py-1 text-xs text-dulia-navy transition hover:bg-dulia-parchment dark:text-dulia-parchment dark:hover:bg-dulia-night"
                >
                  Delete
                </button>
              </div>

              <div className="p-3">
                <Input
                  value={text}
                  onChange={(e) => updateAt(i, e.target.value)}
                  placeholder="e.g. Students"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function isValidDocId(id) {
  if (!id) return false;
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id)) return false;
  return true;
}

export default function SaintForm({
  mode,
  initialValues = {},
  onSubmit,
  submitLabel,
}) {
  const [status, setStatus] = useState("");

  const initialDocId = (initialValues?.id || "").trim();

  const [name, setName] = useState(initialValues.name || "");
  const [stage, setStage] = useState(initialValues.stage || "Saint");

  const [docId, setDocId] = useState(() => {
    if (mode === "edit") return initialDocId || "";
    return slugify(initialValues?.name || "") || "";
  });
  const [docIdTouched, setDocIdTouched] = useState(() => mode === "edit");

  useEffect(() => {
    if (mode !== "create") return;
    if (docIdTouched) return;
    setDocId(slugify(name) || "");
  }, [mode, name, docIdTouched]);

  const [countryValue, setCountryValue] = useState(
    (initialValues.countryLower || "").toLowerCase()
  );

  const [bio, setBio] = useState(initialValues.bio || "");
  const [isPope, setIsPope] = useState(!!initialValues.isPope);

  const [bornText, setBornText] = useState(initialValues.bornText || "");
  const [diedText, setDiedText] = useState(initialValues.diedText || "");

  const [beatifiedISO, setBeatifiedISO] = useState(initialValues.beatifiedISO || "");
  const [canonizedISO, setCanonizedISO] = useState(initialValues.canonizedISO || "");

  const [feastKeys, setFeastKeys] = useState(() => {
    const fromArray =
      initialValues.feastKeys ||
      initialValues.feasts ||
      initialValues.feastKeyList ||
      null;

    if (Array.isArray(fromArray) && fromArray.length) {
      return fromArray.map((k) => parseFeastKey(k)?.key).filter(Boolean);
    }

    if (initialValues.feastKey) {
      const parsed = parseFeastKey(initialValues.feastKey);
      return parsed ? [parsed.key] : [];
    }

    if (initialValues.feastMonth && initialValues.feastDay) {
      const k = monthDayToKey(
        Number(initialValues.feastMonth),
        Number(initialValues.feastDay)
      );
      const parsed = parseFeastKey(k);
      return parsed ? [parsed.key] : [];
    }

    return [];
  });

  const [categorySel, setCategorySel] = useState(() => {
    const arr = Array.isArray(initialValues.categories)
      ? initialValues.categories
      : splitList(initialValues.categories || "");

    const next = new Set(arr.map((x) => String(x || "").trim()).filter(Boolean));

    if (next.has("Pope")) next.delete("Pope");
    if (initialValues.isPope) next.add("Pope");

    return next;
  });

  const [patronageItems, setPatronageItems] = useState(() => {
    const arr = Array.isArray(initialValues.patronage)
      ? initialValues.patronage
      : splitList(initialValues.patronage || "");
    return arr.map((x) => String(x || "").trim()).filter(Boolean);
  });

  const [contributorsItems, setContributorsItems] = useState(() =>
    Array.isArray(initialValues.contributors)
      ? initialValues.contributors.map((c) => String(c || "").trim()).filter(Boolean)
      : splitMultiline(initialValues.contributors)
  );

  const [galleryItems, setGalleryItems] = useState(() => {
    const raw = Array.isArray(initialValues.gallery)
      ? initialValues.gallery
      : splitMultiline(initialValues.gallery);

    return (raw || [])
      .map((it) => {
        if (typeof it === "string") return { url: it, caption: "" };
        if (it && typeof it === "object") {
          return {
            url: it.url || it.src || "",
            caption: it.caption || "",
          };
        }
        return { url: "", caption: "" };
      })
      .map((x) => ({ url: String(x.url || "").trim(), caption: String(x.caption || "") }))
      .filter((x) => !!x.url);
  });

  const [quotesItems, setQuotesItems] = useState(() =>
    Array.isArray(initialValues.quotes)
      ? initialValues.quotes.map((q) => String(q || "").trim()).filter(Boolean)
      : splitMultiline(initialValues.quotes)
  );

  const [prayersItems, setPrayersItems] = useState(() =>
    Array.isArray(initialValues.prayers)
      ? initialValues.prayers.map((p) => String(p || "").trim()).filter(Boolean)
      : splitByBlankLines(initialValues.prayers)
  );

  const slugPreview = useMemo(() => slugify(name) || "...", [name]);
  const docIdPreview = docId || "(empty)";

  function toggleCategory(c) {
    setCategorySel((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);

      if (c === "Pope") setIsPope(next.has("Pope"));
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("");

    const trimmedName = name.trim();
    if (!trimmedName) return setStatus("Name is required.");
    if (!STAGES.includes(stage)) return setStatus("Stage is invalid.");

    const desiredId = String(docId || "").trim();
    if (!desiredId) return setStatus("Document ID is required.");
    if (!isValidDocId(desiredId)) {
      return setStatus(
        "Document ID must be lowercase letters/numbers with hyphens (e.g. therese-of-lisieux)."
      );
    }

    if (!isValidISODate(beatifiedISO))
      return setStatus("Beatified date must be YYYY-MM-DD.");
    if (!isValidISODate(canonizedISO))
      return setStatus("Canonized date must be YYYY-MM-DD.");

    const normalizedFeasts = (feastKeys || [])
      .map((k) => parseFeastKey(k)?.key)
      .filter(Boolean);

    const categoriesArr = Array.from(categorySel)
      .map((x) => String(x || "").trim())
      .filter(Boolean)
      .filter((x) => x !== "Pope");

    const patronageArr = (patronageItems || [])
      .map((p) => String(p || "").trim())
      .filter(Boolean);

    const contributorsArr = (contributorsItems || [])
      .map((c) => String(c || "").trim())
      .filter(Boolean);

    const quotesArr = (quotesItems || [])
      .map((q) => String(q || "").trim())
      .filter(Boolean)
      .map(normalizeOuterDoubleQuotes);

    const prayersArr = (prayersItems || [])
      .map((p) => String(p || "").trim())
      .filter(Boolean);

    const galleryPairs = (galleryItems || [])
      .map((it) => ({
        url: normalizeUrl(it?.url) || "",
        caption: String(it?.caption || ""),
      }))
      .filter((it) => !!it.url);

    for (const it of galleryPairs) {
      if (!isValidUrl(it.url)) return setStatus(`Gallery URL is invalid: ${it.url}`);
    }

    const galleryArr = galleryPairs.map((it) => it.url);
    const galleryCaptionsArr = galleryPairs.map((it) => it.caption.trim() || null);

    const nameTokens = tokensFromName(trimmedName);
    const nameSubstrings = substringsFromTokens(nameTokens);

    const opt = countryOption(countryValue);
    const countryFlag = opt?.flag || null;
    const countryLower = countryValue ? String(countryValue).toLowerCase() : null;

    const patronageLower = patronageArr
      .map((p) => String(p || "").toLowerCase().trim())
      .filter(Boolean);

    const feastPairs = normalizedFeasts.map(parseFeastKey).filter(Boolean);
    const feastMonths = feastPairs.map((f) => f.month);
    const feastDays = feastPairs.map((f) => f.day);
    const firstFeast = feastPairs[0] || null;

    const payload = {
      name: trimmedName,
      nameLower: trimmedName.toLowerCase(),
      nameTokens,
      nameSubstrings,

      stage,
      country: countryFlag,
      countryLower,
      isPope: !!isPope,

      bornText: bornText.trim() || null,
      diedText: diedText.trim() || null,

      beatifiedText: beatifiedISO || null,
      canonizedText: canonizedISO || null,

      feastKeys: feastPairs.map((f) => f.key),
      feastMonths,
      feastDays,

      feastKey: firstFeast ? firstFeast.key : null,
      feastMonth: firstFeast ? firstFeast.month : null,
      feastDay: firstFeast ? firstFeast.day : null,

      categories: categoriesArr,
      patronage: patronageArr,
      patronageLower,

      bio: bio || "",
      quotes: quotesArr,
      prayers: prayersArr,
      gallery: galleryArr,
      galleryCaptions: galleryCaptionsArr,

      contributors: contributorsArr,
    };

    const result = await onSubmit(payload, { docId: desiredId });

    if (result?.ok) setStatus(result.message || "Saved.");
    else setStatus(result?.message || "Something went wrong.");
  }

  const stageInfoMap = STAGE_INFO || {};
  const categoryInfoMap = CATEGORY_INFO || {};

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="overflow-hidden rounded-[30px]">
        <CardHeader className="px-5 py-4">
          <SectionTitle
            eyebrow="Identity"
            title="Profile Basics"
            hint="Enter their name, link slug, country, stage of beatification, and a 2-3 paragraph bio."
          />
        </CardHeader>

        <CardBody className="space-y-5">
        <FieldBlock
              label="Name"
              hint={
                mode === "create"
                  ? `Suggested slug: ${slugPreview}`
                  : undefined
              }
            >
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Saint Peter, Blessed Michael McGivney, Pope Saint John Paul II, etc."
              />
            </FieldBlock>

          <FieldBlock
            label="Slug (what comes after the slash in their profile page link)"
            hint={
              mode === "create"
                ? `Suggested slug comes from the name. Current: ${docIdPreview || slugPreview}`
                : `Changing this will create a new document with the new slug and remove the old one. Current: ${initialDocId || "unknown"}`
            }
          >
            <Input
              value={docId}
              onChange={(e) => {
                setDocIdTouched(true);
                setDocId(e.target.value.toLowerCase());
              }}
              placeholder="saint-therese-of-lisieux"
            />
          </FieldBlock>

          {mode === "create" ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDocIdTouched(true);
                  setDocId(slugify(name) || "");
                }}
              >
                Use suggested slug
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDocIdTouched(true);
                  setDocId("");
                }}
              >
                Clear
              </Button>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
            <FieldBlock label="Country">
              <Select value={countryValue} onChange={(e) => setCountryValue(e.target.value)}>
                <option value="">(none)</option>
                {COUNTRY_SELECT_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>

              {countryValue ? (
                <div className="pt-1">
                  <CountryFlag
                    value={countryValue}
                    showName
                    imgClassName="h-4 w-6 rounded-sm border border-black/10 object-cover"
                    nameClassName="text-xs text-dulia-navy/80 dark:text-dulia-parchment/75"
                  />
                </div>
              ) : null}
            </FieldBlock>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              Stage of Beatification
            </div>
            <div className="flex flex-wrap gap-2">
              {STAGES.map((s) => {
                const active = stage === s;
                const info = stageInfoMap[s];
                return (
                  <HoverBlurb key={s} title={s} blurb={info}>
                    <Chip
                      active={active}
                      onClick={() => setStage(s)}
                      aria-pressed={active}
                      title={info || undefined}
                    >
                      {s}
                    </Chip>
                  </HoverBlurb>
                );
              })}
            </div>
          </div>

          <FieldBlock label="Bio">
            <Textarea
              rows={6}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Write a 2–3 paragraph bio."
            />
          </FieldBlock>
        </CardBody>
      </Card>

      <Card className="overflow-hidden rounded-[30px]">
        <CardHeader className="px-5 py-4">
          <SectionTitle
            eyebrow="Dates"
            title="Life, Recognition, and Feast"
            hint="Enter birthday and death date as free text, then use the date pickers for beatification and canonization dates if applicable. Finally, add any feast days."
          />
        </CardHeader>

        <CardBody className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldBlock label="Born (text)">
              <Input
                value={bornText}
                onChange={(e) => setBornText(e.target.value)}
                placeholder="1 January 2026, c. 33, etc. (Leave empty if unknown)"
              />
            </FieldBlock>

            <FieldBlock label="Died (text)">
              <Input
                value={diedText}
                onChange={(e) => setDiedText(e.target.value)}
                placeholder="1 January 2026, c. 33, etc. (Leave empty if unknown)"
              />
            </FieldBlock>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FieldBlock label="Beatified">
              <Input
                type="date"
                value={beatifiedISO}
                onChange={(e) => setBeatifiedISO(e.target.value)}
              />
            </FieldBlock>

            <FieldBlock label="Canonized">
              <Input
                type="date"
                value={canonizedISO}
                onChange={(e) => setCanonizedISO(e.target.value)}
              />
            </FieldBlock>
          </div>

          <FeastDaysEditor keys={feastKeys} setKeys={setFeastKeys} />
        </CardBody>
      </Card>

      <Card className="overflow-hidden rounded-[30px]">
        <CardHeader className="px-5 py-4">
          <SectionTitle
            eyebrow="Classification"
            title="Tags and Patronage"
            hint="Select any relevant tags and add patronages to help make this profile more discoverable."
          />
        </CardHeader>

        <CardBody className="space-y-5">
          <div className="space-y-3">
            <div className="text-sm font-medium text-dulia-ink dark:text-dulia-parchment">
              Tags
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {CATEGORY_GROUPS.map((group) => (
                <div
                  key={group.label}
                  className="rounded-2xl border border-dulia-line/80 bg-dulia-parchment/55 p-4 dark:border-dulia-lineStrong/20 dark:bg-dulia-night"
                >
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                    {group.label}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {group.options.map((c) => {
                      const active = categorySel.has(c);
                      const info = categoryInfoMap[c];
                      return (
                        <HoverBlurb key={c} title={c} blurb={info}>
                          <Chip
                            active={active}
                            onClick={() => toggleCategory(c)}
                            aria-pressed={active}
                            title={info || undefined}
                          >
                            {c}
                          </Chip>
                        </HoverBlurb>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-xs text-dulia-navy/70 dark:text-dulia-parchment/65">
              Selecting multiple tags marks this saint as being in{" "}
              <span className="font-medium">any</span> of them.
            </div>
          </div>

          <PatronageEditor items={patronageItems} setItems={setPatronageItems} />
        </CardBody>
      </Card>

      <Card className="overflow-hidden rounded-[30px]">
        <CardHeader className="px-5 py-4">
          <SectionTitle
            eyebrow="Content"
            title="Contributors, Quotes, and Prayers"
            hint="List any contributors to this profile (including yourself!), then add any relevant quotes and prayers. You can edit each quote and prayer on its card and drag to reorder."
          />
        </CardHeader>

        <CardBody className="space-y-5">
          <TextListEditor
            label="Contributors"
            help="Add one, none, or many. Use this for people who assisted in creating this profile."
            addHelp='Paste multiple contributors as separate lines, then click "Add".'
            addMode="lines"
            items={contributorsItems}
            setItems={setContributorsItems}
            itemRows={1}
            placeholder={"Add your name here if it's not already listed."}
          />

          <div className="grid gap-5 lg:grid-cols-2">
            <TextListEditor
              label="Quotes"
              help="Add one, none, or many. You can edit each quote card and drag to reorder."
              addHelp='Paste multiple quotes as separate lines, then click "Add".'
              addMode="lines"
              items={quotesItems}
              setItems={setQuotesItems}
              itemRows={2}
              placeholder={
                'Paste quote(s) here...\nOne quote per line.\nQuotation marks will be added automatically.'
              }
            />

            <TextListEditor
              label="Prayers"
              help="Add one, none, or many. Each prayer can be multi-paragraph."
              addHelp='Paste multiple prayers separated by a blank line, then click "Add".'
              addMode="blankLines"
              items={prayersItems}
              setItems={setPrayersItems}
              itemRows={6}
              placeholder={
                "Paste prayer(s) here...\n\nSeparate multiple prayers with a blank line."
              }
            />
          </div>
        </CardBody>
      </Card>

      <GalleryEditor items={galleryItems} setItems={setGalleryItems} />

      {status ? (
        <div className="rounded-2xl border border-dulia-line bg-dulia-parchment/70 px-4 py-3 text-sm text-dulia-ink dark:border-dulia-lineStrong/20 dark:bg-dulia-night dark:text-dulia-parchment">
          {status}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary">
          {submitLabel || "Save"}
        </Button>
      </div>
    </form>
  );
}
